import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export default function AboutUs() {
    const { t } = useTranslation();
    const isRTL = t("dir") === "rtl";

    const features = [
        t("Feature 1"),
        t("Feature 2"),
        t("Feature 3"),
        t("Feature 4"),
        t("Feature 5"),
        t("Feature 6"),
        t("Feature 7"),
        t("Feature 8"),
        t("Feature 9"),
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 py-6 px-4" dir={t("dir")}>
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="max-w-6xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200"
            >
                {/* Header Section */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-4 px-6 border-b border-white/10"
                >
                    <div className="flex items-center justify-between">
                        <motion.div
                            initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                            className="flex items-center gap-3"
                        >
                            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-xl md:text-2xl font-bold">
                                    {t("About Software")}
                                </h1>
                                <p className="text-blue-100 text-xs md:text-sm font-medium mt-0.5">
                                    {t("Version")} 2.1.0
                                </p>
                            </div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.4 }}
                            className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20"
                        >
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                            <span className="text-xs font-medium text-white/90">{t("Active")}</span>
                        </motion.div>
                    </div>
                </motion.div>

                {/* Main Content */}
                <div className="p-6 md:p-4">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-start">
                        {/* Info Text - First in RTL, Second in LTR */}
                        {isRTL && (
                            <motion.div
                                initial={{ opacity: 0, x: -40 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.4 }}
                                className="lg:col-span-3 lg:col-start-1 space-y-6 text-gray-700"
                            >
                                {/* Description */}
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border-l-4 border-blue-500">
                                    <p className="leading-relaxed text-justify text-gray-700">
                                        {t("TISS ENGINE Description")}
                                    </p>
                                </div>

                                {/* API Info */}
                                <div className="bg-gray-50 p-5 rounded-xl shadow-inner border border-gray-200">
                                    <div className="space-y-2">
                                        <p className="text-sm">
                                            <strong className="text-gray-800">{t("API URL")}:</strong>{" "}
                                            <span className="text-blue-600 font-mono text-xs break-all">
                                                {process.env.REACT_APP_BACKEND_URL || "Not configured"}
                                            </span>
                                        </p>
                                        <p className="text-sm">
                                            <strong className="text-gray-800">{t("CDN")}:</strong>{" "}
                                            <span className="text-blue-600 font-mono text-xs break-all">
                                                {process.env.REACT_APP_FRONT_URL || "Not configured"}
                                            </span>
                                        </p>
                                    </div>
                                </div>

                                {/* Developer */}
                                <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                                        EP
                                    </div>
                                    <p className="text-gray-700">
                                         {/* {t("Developed by")}{" "}
                                        <span className="text-blue-600 font-semibold">Esmail Pouryousef</span> */}
                                    </p>
                                </div>

                                {/* Features */}
                                <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
                                    <h3 className="font-bold text-xl mb-4 text-gray-800 flex items-center gap-2">
                                        <span className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded"></span>
                                        {t("TISS ENGINE 2.0 Features")}
                                    </h3>
                                    <ul className="space-y-2.5">
                                        {features.map((feature, index) => (
                                            <motion.li
                                                key={index}
                                                initial={{ opacity: 0, x: 20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.5 + index * 0.05 }}
                                                className="flex items-start gap-3 text-sm text-gray-700"
                                            >
                                                <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                                                <span className="leading-relaxed">{feature}</span>
                                            </motion.li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Copyright Section */}
                                <div className="space-y-4 pt-4 border-t-2 border-gray-200">
                                    <div className="bg-amber-50 p-5 rounded-xl border-l-4 border-amber-400">
                                        <p className="text-sm leading-relaxed text-justify text-gray-700 mb-3">
                                            {t("Copyright Text 1")}
                                        </p>
                                        <p className="text-sm leading-relaxed text-justify text-gray-700 mb-3">
                                            {t("Copyright Text 2")}
                                        </p>
                                        <p className="text-sm leading-relaxed text-justify text-gray-700">
                                            {t("Copyright Text 3")}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* Image + Logos */}
                        <motion.div
                            initial={{ opacity: 0, x: isRTL ? 40 : -40 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                            className={`${isRTL ? 'lg:col-span-1 lg:col-start-4' : 'lg:col-span-1 lg:col-start-1'} flex flex-col items-center`}
                        >
                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                transition={{ type: "spring", stiffness: 300 }}
                                className="relative overflow-hidden rounded-2xl shadow-xl"
                            >
                                <img
                                    src="/intro.jpg"
                                    alt="Preview"
                                    className="w-full h-auto rounded-2xl"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
                            </motion.div>
                            
                            <div className="mt-6 flex gap-4 flex-wrap justify-center">
                                {[
                                    "/about/express.svg",
                                    "/about/mongo.svg",
                                    "/about/node.svg",
                                    "/about/react.svg",
                                    "/about/tailwinds.svg",
                                ].map((icon, index) => (
                                    <motion.div
                                        key={index}
                                        whileHover={{ scale: 1.15, rotate: 5 }}
                                        whileTap={{ scale: 0.95 }}
                                        transition={{ type: "spring", stiffness: 300 }}
                                        className="bg-white p-1 rounded-xl shadow-md hover:shadow-xl transition-shadow cursor-pointer"
                                    >
                                        <img
                                            src={icon}
                                            alt="Tech Icon"
                                            width={25}
                                            height={25}
                                            className="opacity-90 hover:opacity-100"
                                        />
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Info Text - Second in LTR */}
                        {!isRTL && (
                            <motion.div
                                initial={{ opacity: 0, x: 40 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.4 }}
                                className="lg:col-span-3 lg:col-start-2 space-y-6 text-gray-700"
                            >
                            {/* Description */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border-l-4 border-blue-500">
                                <p className="leading-relaxed text-justify text-gray-700">
                                    {t("TISS ENGINE Description")}
                                </p>
                            </div>

                            {/* API Info */}
                            <div className="bg-gray-50 p-5 rounded-xl shadow-inner border border-gray-200">
                                <div className="space-y-2">
                                    <p className="text-sm">
                                        <strong className="text-gray-800">{t("API URL")}:</strong>{" "}
                                        <span className="text-blue-600 font-mono text-xs break-all">
                                            {process.env.REACT_APP_BACKEND_URL || "Not configured"}
                                        </span>
                                    </p>
                                    <p className="text-sm">
                                        <strong className="text-gray-800">{t("CDN")}:</strong>{" "}
                                        <span className="text-blue-600 font-mono text-xs break-all">
                                            {process.env.REACT_APP_FRONT_URL || "Not configured"}
                                        </span>
                                    </p>
                                </div>
                            </div>

                            {/* Developer */}
                            <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                                    EP
                                </div>
                                <p className="text-gray-700">
                                    {t("Developed by")}{" "}
                                    <span className="text-blue-600 font-semibold">Esmail Pouryousef</span>
                                </p>
                            </div>

                            {/* Features */}
                            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
                                <h3 className="font-bold text-xl mb-4 text-gray-800 flex items-center gap-2">
                                    <span className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded"></span>
                                    {t("TISS ENGINE 2.0 Features")}
                                </h3>
                                <ul className="space-y-2.5">
                                    {features.map((feature, index) => (
                                        <motion.li
                                            key={index}
                                            initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.5 + index * 0.05 }}
                                            className="flex items-start gap-3 text-sm text-gray-700"
                                        >
                                            <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                                            <span className="leading-relaxed">{feature}</span>
                                        </motion.li>
                                    ))}
                                </ul>
                            </div>

                            {/* Copyright Section */}
                            <div className="space-y-4 pt-4 border-t-2 border-gray-200">
                                <div className="bg-amber-50 p-5 rounded-xl border-l-4 border-amber-400">
                                    <p className="text-sm leading-relaxed text-justify text-gray-700 mb-3">
                                        {t("Copyright Text 1")}
                                    </p>
                                    <p className="text-sm leading-relaxed text-justify text-gray-700 mb-3">
                                        {t("Copyright Text 2")}
                                    </p>
                                    <p className="text-sm leading-relaxed text-justify text-gray-700">
                                        {t("Copyright Text 3")}
                                    </p>
                                </div>
                            </div>
                            </motion.div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-4 px-6 text-center"
                >
                    <p className="font-medium">
                        © 2024. {t("All rights reserved")}
                    </p>
                </motion.div>
            </motion.div>
        </div>
    );
}
